/**
 * CTODOCX API Service - COMPLETE (All 20 Backend Routes)
 * Updated: Uses ID Token for API Gateway Custom Authorizer
 * Backend reads user identity from validated JWT claims
 * 
 * FIXED: Upload flow, CORS handling, owner tracking, share links
 */

import { msalConfig } from "../authConfig";
import { PublicClientApplication } from "@azure/msal-browser";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// ===============================================
// SHARE LINK - Backend-managed share tokens.
// POST /share/{id}    -> create token-based share URL
// GET  /share/resolve -> resolve token, check permission
// DELETE /share       -> revoke a share link
// ===============================================

let msalInstance = null;
let currentUser = null;

export function initApiService(instance) {
  msalInstance = instance;
  const account = instance.getActiveAccount();
  if (account) {
    currentUser = {
      name: account.name || account.username || "Unknown",
      email: account.username || "",
    };
  }
}

export function getCurrentUser() {
  return currentUser;
}

async function getToken() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error("No active account. Please login again.");

  currentUser = {
    name: account.name || account.username || "Unknown",
    email: account.username || "",
  };

  try {
    const resp = await msalInstance.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account,
    });
    return resp.idToken;
  } catch {
    const resp = await msalInstance.acquireTokenPopup({
      scopes: ["openid", "profile", "email"],
      account,
    });
    return resp.idToken;
  }
}

async function apiCall(method, path, body = null) {
  const token = await getToken();
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  };
  if (body) options.body = JSON.stringify(body);

  const url = `${API_BASE}${path}`;

  const res = await fetch(url, options);
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = { message: text };
  }

  if (!res.ok) {
    throw new Error(data.error || data.message || `API Error: ${res.status}`);
  }

  return data;
}

// ═══════════════════════════════════════════════
// FOLDER Operations (3 routes)
// ═══════════════════════════════════════════════

export async function createFolder(parentPath, folderName) {
  return apiCall("POST", "/folders", {
    folderName,
    parentPath,
  });
}

export async function listFolder(folderPath) {
  const encodedPath = encodeURIComponent(folderPath);
  return apiCall("GET", `/folders?path=${encodedPath}`);
}

export async function deleteFolder(folderName, parentPath) {
  return apiCall("DELETE", "/folders", {
    folderName,
    parentPath,
    force: true,
  });
}

// ═══════════════════════════════════════════════
// DOCUMENT Operations (5 routes)
// ═══════════════════════════════════════════════

export async function createDocument(fileName, folderPath, contentType, fileSize, description = "", tags = []) {
  const body = {
    fileName,
    folderPath,
    contentType,
    fileSize,
  };
  if (description) body.description = description;
  if (tags.length > 0) body.tags = tags;
  return apiCall("POST", "/documents", body);
}

export async function listDocuments(folderPath) {
  const encodedPath = encodeURIComponent(folderPath);
  return apiCall("GET", `/documents?path=${encodedPath}`);
}

export async function getDocument(docId) {
  return apiCall("GET", `/documents/${encodeURIComponent(docId)}`);
}

export async function updateDocument(docId, updates) {
  return apiCall("PUT", `/documents/${encodeURIComponent(docId)}`, updates);
}

export async function deleteDocument(docId) {
  return apiCall("DELETE", `/documents/${encodeURIComponent(docId)}`);
}

// ═══════════════════════════════════════════════
// UPLOAD / DOWNLOAD Operations (3 routes)
// ═══════════════════════════════════════════════

export async function getUploadUrl(fileName, folderPath, contentType, documentId) {
  return apiCall("POST", "/upload", { fileName, folderPath, contentType, documentId });
}

export async function confirmUpload(documentId, folderPath) {
  return apiCall("POST", "/upload/confirm", { documentId, folderPath });
}

export async function downloadFile(docId, fileName) {
  const result = await apiCall("GET", `/download/${encodeURIComponent(docId)}`);
  const url = result.downloadUrl || result.download?.url || result.url || result.presignedUrl || "";
  if (!url) throw new Error("No download URL received");

  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName || result.fileName || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
    return { success: true, url };
  } catch (fetchErr) {
    window.open(url, "_blank");
    return { success: true, url };
  }
}

export async function getDownloadUrl(docId) {
  const result = await apiCall("GET", `/download/${encodeURIComponent(docId)}`);
  const url = result.downloadUrl || result.download?.url || result.url || result.presignedUrl || "";
  return url;
}

/**
 * FULL UPLOAD FLOW — Uses createDocument (which returns presigned URL)
 * then uploads to S3, then confirms.
 * 
 * IMPORTANT: We use createDocument route (POST /documents) which:
 * 1. Creates DynamoDB entry with status "pending_upload"
 * 2. Returns presigned upload URL
 * Then we upload to S3, then confirm.
 * 
 * We do NOT call POST /upload separately — that would create a DUPLICATE entry.
 */
export async function uploadFile(file, folderPath, onProgress) {
  try {
  const contentType = file.type || "application/octet-stream";

  // File size validation (50MB max)
  const MAX_SIZE = 50 * 1024 * 1024;
  if (file.size > MAX_SIZE) {
    throw new Error("File too large. Maximum 50MB allowed.");
  }

  // Step 1: Create document metadata (backend creates DynamoDB entry + returns presigned URL)
  const createResult = await createDocument(file.name, folderPath, contentType, file.size);
  const docId = createResult.document?.id || createResult.documentId || createResult.document?.item_id;

  if (!docId) throw new Error("Failed to create document - no ID returned");

  // Step 2: Get presigned upload URL from createDocument response
  let presignedUrl = createResult.uploadUrl;
  if (!presignedUrl) {
    // Fallback: call /upload route (only if createDocument didn't return URL)
    const uploadResult = await getUploadUrl(file.name, folderPath, contentType, docId);
    presignedUrl = uploadResult.uploadUrl || uploadResult.url || (uploadResult.upload && uploadResult.upload.url);
  }

  if (!presignedUrl) throw new Error("Failed to get upload URL");

  // Step 3: Upload file directly to S3 via presigned URL
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: ${xhr.status}`));
    });
    xhr.addEventListener("error", () => reject(new Error("Upload network error")));
    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(file);
  });

  // Step 4: Confirm upload — backend verifies the file exists in S3 and
  // flips status from "pending_upload" to "active". Without this the file
  // stays pending and never appears in the list (spinner never stops).
  try {
    await confirmUpload(docId, folderPath);
  } catch (confirmErr) {
    throw new Error(`Upload confirm failed: ${confirmErr.message}`);
  }

  return { success: true, documentId: docId, fileName: file.name };
  } catch (err) {
    throw new Error(err.message || "Upload failed. Please try again.");
  }
}

// ═══════════════════════════════════════════════
// SEARCH (1 route)
// ═══════════════════════════════════════════════

export async function searchDocuments(query, folderPath, type) {
  let endpoint = `/search?q=${encodeURIComponent(query)}`;
  if (folderPath && folderPath !== "/") endpoint += `&path=${encodeURIComponent(folderPath)}`;
  if (type) endpoint += `&type=${encodeURIComponent(type)}`;
  return apiCall("GET", endpoint);
}

// ═══════════════════════════════════════════════
// PERMISSIONS (2 routes)
// ═══════════════════════════════════════════════

export async function getPermissions(docId) {
  return apiCall("GET", `/permissions/${encodeURIComponent(docId)}`);
}

export async function setPermissions(docId, permissions) {
  return apiCall("POST", `/permissions/${encodeURIComponent(docId)}`, permissions);
}

// ═══════════════════════════════════════════════
// ACTIVITY LOG (3 routes)
// ═══════════════════════════════════════════════

export async function getActivity(docId) {
  return apiCall("GET", `/activity/${encodeURIComponent(docId)}`);
}

export async function getRecentActivity() {  
  return apiCall("GET", "/activity/all");
}

export async function logActivity(docId, action, details) {
  return apiCall("POST", `/activity/${encodeURIComponent(docId)}`, {
    action,
    details,
    timestamp: new Date().toISOString(),
  });
}

// ═══════════════════════════════════════════════
// SHARE LINKS — Token-based (4 routes)
// ═══════════════════════════════════════════════

export async function createShareLink(docId, options = {}) {
  return apiCall("POST", `/share/${encodeURIComponent(docId)}`, {
    expires_days: options.expires_days || 30,
    max_access_count: options.max_access_count || null,
  });
}

export async function resolveShareLink(shareToken) {
  const param = "token";
  const endpoint = "/share/resolve?" + param + "=" + encodeURIComponent(shareToken);
  return apiCall("GET", endpoint);
}

export async function getShareLinks(docId) {
  return apiCall("GET", `/share/${encodeURIComponent(docId)}`);
}

export async function deleteShareLink(linkId) {
  return apiCall("DELETE", "/share", { link_id: linkId });
}

// Legacy alias
export async function searchFiles(query) {
  return searchDocuments(query);
}
